import * as React from 'react';
import { canManagePeople, IAppInfo, IPortalMe } from './auth';
import { CubeCluster, EmptyArt, Icon, SeltzerArt } from './brand';
import { Link, Shell } from './Shell';

// /apps - the launcher: only the apps enabled for the signed-in user's organization.

// Bastion's mark: an arrowhead bastion (star-fort point) seen from above.
const BastionArt: React.FunctionComponent = () => (
  <svg width={104} height={104} viewBox="0 0 32 32" aria-hidden="true">
    <path d="M16 2.5 29 11.6 24.4 29H7.6L3 11.6Z" fill="none" stroke="#b08d57" strokeWidth="2.4" strokeLinejoin="round" />
    <path d="M16 9.5 22.6 14.2 20.3 23H11.7L9.4 14.2Z" fill="#b08d57" />
  </svg>
);

// Support's mark: a headset (the BeefedUp IT support desk), in the BeefedUp crimson.
const SupportArt: React.FunctionComponent = () => (
  <svg width={104} height={104} viewBox="0 0 32 32" aria-hidden="true">
    <path d="M6 18v-3a10 10 0 0 1 20 0v3" fill="none" stroke="#a8191f" strokeWidth="2.4" strokeLinecap="round" />
    <rect x="3.5" y="16.5" width="6" height="9" rx="2.2" fill="#a8191f" />
    <rect x="22.5" y="16.5" width="6" height="9" rx="2.2" fill="#a8191f" />
    <path d="M25.5 25.5c0 2.6-2.4 3.8-6.5 3.8" fill="none" stroke="#a8191f" strokeWidth="2" strokeLinecap="round" />
    <rect x="15" y="27.6" width="5" height="3.4" rx="1.7" fill="#a8191f" />
  </svg>
);

const ART: { [key: string]: React.ReactElement } = { seltzer: <SeltzerArt height={104} />, bastion: <BastionArt />, support: <SupportArt /> }; // seltzer = BatchWorks

const greeting = (): string => { const h: number = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

const Portal: React.FunctionComponent<{ me: IPortalMe }> = ({ me }) => {
  const first: string = (me.name || me.email).split(/[\s@]/)[0];
  return (
    <Shell me={me} active="apps">
      <main className="bu-page">
        {me.client === null ? (
          <div className="bu-card bu-empty">
            <EmptyArt />
            <h3>Your organization isn't set up yet</h3>
            <p style={{ maxWidth: 480, margin: '0 auto' }}>
              You're signed in as <strong>{me.email}</strong>, but your organization doesn't have BeefedUp Apps yet.
              Contact BeefedUp IT and we'll get you set up.
            </p>
            <p className="bu-mono" style={{ color: 'var(--text-3)', marginTop: 14 }}>Organization ID {me.tenantId}</p>
            {me.isOwner && <p><Link to="/admin/clients" className="bu-btn bu-btn-primary" >Open client admin</Link></p>}
          </div>
        ) : (
          <React.Fragment>
            <section className="bu-welcome">
              <CubeCluster className="bu-welcome-art" />
              <p className="bu-eyebrow">{me.client.name}</p>
              <h1>{greeting()}, {first}.</h1>
              <p>{me.role === null
                ? `You don't have a role in ${me.client.name} yet. Ask one of its admins to invite you.`
                : me.apps.length === 0 ? 'No apps are enabled for your organization yet.' : `You have ${me.apps.length} app${me.apps.length === 1 ? '' : 's'} available as ${me.role.name}.`}</p>
            </section>

            <div className="bu-page-head" style={{ marginBottom: 18 }}>
              <h2 className="bu-card-title" style={{ fontSize: 18 }}>Your apps</h2>
            </div>
            <div className="bu-apps">
              {me.apps.map((a: IAppInfo) => {
                const body: React.ReactElement = (
                  <React.Fragment>
                    <div className="bu-app-art">{ART[a.key] || <Icon name="grid" size={40} />}</div>
                    <div className="bu-app-body">
                      <h3>{a.name}</h3>
                      <p>{a.description}</p>
                      <div className="bu-app-foot">Open app <Icon name="arrow" size={16} /></div>
                    </div>
                  </React.Fragment>
                );
                // Apps hosted on their own (Bastion, Support) open at their address and sign the person in there.
                return a.url
                  ? <a key={a.key} href={a.url} className="bu-card bu-app">{body}</a>
                  : <Link key={a.key} to={a.path} className="bu-card bu-app">{body}</Link>;
              })}
            </div>

            {canManagePeople(me) && (
              <div className="bu-card bu-card-pad" style={{ marginTop: 28, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div className="bu-feature-icon" style={{ margin: 0 }}><Icon name="users" /></div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <strong style={{ fontFamily: 'var(--font-display)', fontSize: 15 }}>Your team in {me.client.name}</strong>
                  <div style={{ color: 'var(--text-2)', fontSize: 14, marginTop: 2 }}>Invite people by email and choose exactly what each role can see and do.</div>
                </div>
                <Link to="/admin/team" className="bu-btn bu-btn-secondary">People &amp; roles</Link>
              </div>
            )}
          </React.Fragment>
        )}
      </main>
    </Shell>
  );
};

export default Portal;
